import React, { useEffect, useState, useRef } from 'react'; // Import useRef
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Modal, Spin, message, Upload, Tooltip, Checkbox } from 'antd'; // Import Upload, Tooltip, Checkbox
import { PlusOutlined, DeleteOutlined, UploadOutlined, InfoCircleOutlined } from '@ant-design/icons'; // Import UploadOutlined, InfoCircleOutlined
import { getAreas, createArea, deleteArea } from '../../services/areaAPI';
import { setAreas, setLoading, setError } from '../../redux/slices/areaSlice';
import * as XLSX from 'xlsx'; // Import xlsx library

const AreaManagementPage = () => {
  const dispatch = useDispatch();
  const { areas, loading, error } = useSelector((state) => state.area);

  const [modalVisible, setModalVisible] = useState(false); // Modal for adding a single area
  const [newArea, setNewArea] = useState({ name: '', description: '' });
  const [excelUploadLoading, setExcelUploadLoading] = useState(false); // Loading state for Excel upload
  const fileInputRef = useRef(null); // Ref for the hidden file input

  // State for multiselect delete
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false); // Loading state for delete operations

  useEffect(() => {
    fetchAreas();
  }, []);

  const fetchAreas = async () => {
    dispatch(setLoading(true));
    try {
      const data = await getAreas();
      // Sort areas by creation date or name if needed, otherwise map index will be based on current order
      dispatch(setAreas(data));
    } catch (err) {
      dispatch(setError(err.message));
      message.error(`Failed to fetch areas: ${err.message}`);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCreateArea = async () => {
    if (!newArea.name.trim()) {
      message.error('Area name is required');
      return;
    }
    try {
      await createArea(newArea);
      message.success('Area created successfully');
      setModalVisible(false);
      setNewArea({ name: '', description: '' });
      fetchAreas(); // Refresh the list after creating
    } catch (err) {
      message.error(`Error creating area: ${err.message}`);
    }
  };

  // Handler for single area delete
  const handleDeleteArea = async (id) => {
    setDeleteLoading(true); // Start loading for delete
    try {
      await deleteArea(id);
      message.success('Area deleted successfully');
      fetchAreas(); // Refresh the list after deleting
      setSelectedAreaIds(selectedAreaIds.filter(areaId => areaId !== id)); // Remove deleted ID from selection
    } catch (err) {
      message.error(`Error deleting area: ${err.message}`);
    } finally {
      setDeleteLoading(false); // End loading
    }
  };

  // Handler for deleting multiple selected areas
  const handleDeleteSelectedAreas = async () => {
    if (selectedAreaIds.length === 0) {
      message.warning('No areas selected for deletion.');
      return;
    }

    setDeleteLoading(true); // Start loading for delete
    try {
      // Use Promise.all to send delete requests concurrently
      const deletePromises = selectedAreaIds.map(id => deleteArea(id));
      await Promise.all(deletePromises);

      message.success(`${selectedAreaIds.length} areas deleted successfully.`);
      setSelectedAreaIds([]); // Clear selection after deletion
      fetchAreas(); // Refresh the list
    } catch (err) {
      message.error(`Error deleting selected areas: ${err.message}`);
    } finally {
      setDeleteLoading(false); // End loading
    }
  };


  // Handler for Excel file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setExcelUploadLoading(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const bstr = e.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        // Read data starting from the first row, getting all columns
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false }); // Use raw: false to get formatted values

        if (data.length === 0) {
          message.warning('Excel file is empty.');
          return;
        }

        const areasToCreate = [];

        // Iterate through rows to extract name and description
        for (const row of data) {
          const areaName = row[0] ? String(row[0]).trim() : ''; // Get name from first column, handle potential non-string types
          const areaDescription = row[1] ? String(row[1]).trim() : ''; // Get description from second column, handle potential non-string types

          if (areaName) { // Only process rows with a valid area name
            areasToCreate.push({ name: areaName, description: areaDescription });
          }
          // Rows with description but no name are ignored as per requirement
        }


        if (areasToCreate.length === 0) {
          message.warning('No valid areas found in the first column of the Excel file.');
          return;
        }

        // Create area for each extracted object
        const creationPromises = areasToCreate.map(areaData => {
          return createArea(areaData); // Call your create area API with name and description
        });

        await Promise.all(creationPromises); // Wait for all areas to be created

        message.success(`${areasToCreate.length} areas created successfully from Excel.`);
        fetchAreas(); // Refresh the area list
      } catch (error) {
        console.error('Excel processing or area creation error:', error);
        message.error(`Failed to create areas from Excel: ${error.message}`);
      } finally {
        setExcelUploadLoading(false);
        // Reset the file input value to allow uploading the same file again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    reader.readAsBinaryString(file);
  };

  // Trigger hidden file input click
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handler for individual checkbox change
  const handleCheckboxChange = (areaId, checked) => {
    setSelectedAreaIds(prevSelected =>
      checked ? [...prevSelected, areaId] : prevSelected.filter(id => id !== areaId)
    );
  };

  // Handler for "Select All" checkbox change
  const handleSelectAllChange = (e) => {
    if (e.target.checked) {
      setSelectedAreaIds(areas.map(area => area._id));
    } else {
      setSelectedAreaIds([]);
    }
  };

  // Determine if "Select All" checkbox is checked
  const isSelectAllChecked = areas.length > 0 && selectedAreaIds.length === areas.length;
  // Determine if "Select All" checkbox is indeterminate
  const isSelectAllIndeterminate = selectedAreaIds.length > 0 && selectedAreaIds.length < areas.length;


  return (
    <div className="p-2 min-h-screen">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4"> {/* Added gap */}
          <h2 className="text-2xl font-semibold text-gray-800">Area Management</h2>
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"> {/* Group buttons */}
             {/* Delete Selected Button */}
             {selectedAreaIds.length > 0 && (
                <Button
                  type="danger"
                  icon={<DeleteOutlined />}
                  onClick={handleDeleteSelectedAreas}
                  loading={deleteLoading}
                  disabled={deleteLoading}
                  className="w-full sm:w-auto"
                >
                  Delete Selected ({selectedAreaIds.length})
                </Button>
              )}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)} className="w-full sm:w-auto">
              Add Area
            </Button>
            <Button
              type="default"
              icon={<UploadOutlined />}
              onClick={triggerFileInput}
              loading={excelUploadLoading}
              disabled={excelUploadLoading}
              className="w-full sm:w-auto flex items-center justify-center" // Added flex and justify-center
            >
              Upload from Excel
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </Button>
            {/* Info Button for Excel Upload */}
            <Tooltip title="Your Excel file should contain a list of area names in the first column (Column A) and descriptions in the second column (Column B), starting from the first row. No header row is required. Rows without a name in Column A will be ignored.">
              <Button
                type="text" // Use text type for a less prominent icon button
                icon={<InfoCircleOutlined className="text-gray-500 hover:text-gray-700" />} // Info icon
                className="flex items-center justify-center" // Center icon
              />
            </Tooltip>
          </div>
        </div>

        {loading && areas.length === 0 ? ( // Check loading specifically for areas table
          <div className="flex justify-center py-10">
            <Spin size="large" />
          </div>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : areas.length === 0 ? (
          <p className="text-gray-600 text-center py-8">No areas found. Start by adding an area or uploading from Excel.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-gray-200 shadow-sm"> {/* Added table styling */}
            <table className="min-w-full bg-white divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                   {/* Checkbox Header for multiselect */}
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     <Checkbox
                       checked={isSelectAllChecked}
                       indeterminate={isSelectAllIndeterminate}
                       onChange={handleSelectAllChange}
                       disabled={areas.length === 0} // Disable if no areas
                     />
                   </th>
                   {/* Added Serial No. header */}
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial No.</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Added index for serial number */}
                {areas.map((area, index) => (
                  <tr key={area._id}>
                    {/* Checkbox for multiselect */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <Checkbox
                        checked={selectedAreaIds.includes(area._id)}
                        onChange={(e) => handleCheckboxChange(area._id, e.target.checked)}
                      />
                    </td>
                    {/* Added Serial No. data */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{area.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{area.description || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteArea(area._id)}
                        disabled={deleteLoading} // Disable individual delete while batch deleting
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Area Modal */}
        <Modal
          title="Create New Area"
          open={modalVisible} // Use 'open' instead of 'visible' for Ant Design v5+
          onCancel={() => setModalVisible(false)}
          onOk={handleCreateArea}
          okText="Create"
          confirmLoading={loading} // Optional: Add loading to modal OK button if createArea sets global loading
        >
          <Input
            className="mb-4"
            placeholder="Area Name"
            value={newArea.name}
            onChange={(e) => setNewArea({ ...newArea, name: e.target.value })}
            onPressEnter={handleCreateArea} // Allow creating with Enter key
          />
          <Input.TextArea
            rows={4}
            placeholder="Area Description (optional)"
            value={newArea.description}
            onChange={(e) => setNewArea({ ...newArea, description: e.target.value })}
          />
        </Modal>

      </div>
    </div>
  );
};

export default AreaManagementPage;
